import { motion } from 'framer-motion';
import { ChevronRight, Phone, Mail, MapPin, Clock, MessageCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

const ContactPage = () => {
  const whatsappUrl = 'https://wa.me/40759649614?text=Bună!%20Am%20o%20întrebare%20despre%20sneakers.';

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Header */}
      <div className="py-24 px-6 md:px-12 lg:px-24 border-b border-white/5">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center gap-2 font-body text-sm text-white/50 mb-6">
            <Link to="/" className="hover:text-white transition-colors">Acasă</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-white">Contact</span>
          </div>
          
          <h1 className="font-heading font-bold text-4xl md:text-6xl tracking-tight uppercase text-white" data-testid="contact-title">
            CONTACTEAZĂ-NE
          </h1>
          <p className="font-body text-white/50 mt-4 max-w-2xl">
            Ai o întrebare? Suntem aici să te ajutăm. Contactează-ne pe WhatsApp pentru cel mai rapid răspuns.
          </p>
        </motion.div>
      </div>

      {/* Content */}
      <div className="px-6 md:px-12 lg:px-24 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl">
          {/* Contact Methods */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
          >
            <h2 className="font-heading font-bold text-xl tracking-wider text-white mb-8">CONTACTEAZĂ-NE</h2>
            
            {/* WhatsApp - Primary */}
            <a 
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="block bg-[#25D366] p-8 mb-6 group hover:bg-[#20BA5A] transition-colors"
            >
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-white/20 flex items-center justify-center">
                  <MessageCircle className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-xl text-white">WhatsApp</h3>
                  <p className="font-body text-white/90">+40 759 649 614</p>
                  <p className="font-body text-sm text-white/70 mt-1">Cel mai rapid răspuns • Disponibil zilnic</p>
                </div>
              </div>
            </a>

            {/* Other Contact Methods */}
            <div className="space-y-4">
              <div className="bg-[#121212] border border-white/5 p-6 flex items-center gap-4">
                <div className="w-12 h-12 bg-[#c00000]/10 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-[#c00000]" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-white">Telefon</h3>
                  <p className="font-body text-white/60">+40 759 649 614</p>
                </div>
              </div>

              <div className="bg-[#121212] border border-white/5 p-6 flex items-center gap-4">
                <div className="w-12 h-12 bg-[#c00000]/10 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-[#c00000]" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-white">Email</h3>
                  <p className="font-body text-white/60">contact@deprokicks.com</p>
                </div>
              </div>

              <div className="bg-[#121212] border border-white/5 p-6 flex items-center gap-4">
                <div className="w-12 h-12 bg-[#c00000]/10 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5 text-[#c00000]" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-white">Program</h3>
                  <p className="font-body text-white/60">Luni - Sâmbătă: 10:00 - 20:00</p>
                  <p className="font-body text-white/60">Duminică: 12:00 - 18:00</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* FAQ Section */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <h2 className="font-heading font-bold text-xl tracking-wider text-white mb-8">ÎNTREBĂRI FRECVENTE</h2>
            
            <div className="space-y-4">
              <div className="bg-[#121212] border border-white/5 p-6">
                <h3 className="font-heading font-bold text-white mb-2">Cât durează livrarea?</h3>
                <p className="font-body text-white/60">
                  Livrarea în România durează 2-4 zile lucrătoare. Toate comenzile includ transport gratuit.
                </p>
              </div>

              <div className="bg-[#121212] border border-white/5 p-6">
                <h3 className="font-heading font-bold text-white mb-2">Pot plăti la livrare?</h3>
                <p className="font-body text-white/60">
                  Da! Oferim Plata la Livrare (Ramburs) cu posibilitatea de a inspecta coletul. Poți verifica comanda înainte de plată.
                </p>
              </div>

              <div className="bg-[#121212] border border-white/5 p-6">
                <h3 className="font-heading font-bold text-white mb-2">Care este politica de retur?</h3>
                <p className="font-body text-white/60">
                  Poți returna produsele în 14 zile fără motiv, conform legislației române (OUG 34/2014). <Link to="/return-policy" className="text-[#c00000] hover:underline">Citește mai mult</Link>
                </p>
              </div>

              <div className="bg-[#121212] border border-white/5 p-6">
                <h3 className="font-heading font-bold text-white mb-2">Produsele sunt autentice?</h3>
                <p className="font-body text-white/60">
                  Garantăm sneakers de calitate premium. Toate produsele sunt inspectate cu atenție înainte de expediere.
                </p>
              </div>

              <div className="bg-[#121212] border border-white/5 p-6">
                <h3 className="font-heading font-bold text-white mb-2">Cum îmi urmăresc comanda?</h3>
                <p className="font-body text-white/60">
                  După plasarea comenzii, o poți urmări din pagina contului tău. De asemenea, trimitem actualizări pe WhatsApp.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
